import { useCallback, useMemo, useRef, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { EnforcementState, TenantFeatureFlags, LockedPolicy, SiteOverride, SiteOverrides, CatalogSite, TenantNotificationPreferences } from "../types";

type Result = { success: boolean; error?: string };
// Per-control save feedback (ENG-2469): a toggle/select shows its own state so a slow round-trip
// never reads as a dead click.
export type SaveStatus = "saving" | "saved" | "error";

// Business Settings data hook (ENG-2469). Admin-only: fetches + mutates the caller's OWN-tenant
// enforcement mode, feature flags, and locked policy - each a Firebase-auth /business/* endpoint with
// the tenant resolved server-side from the token. Fails honest: a failed load leaves the section empty
// rather than fabricating, and each mutator returns {success,error} so the UI can surface the reason
// (notably the 409 when a tenant admin tries to switch to Active while inside the forced-monitor lock).
//
// Toggles + the enforcement select apply optimistically: local state flips immediately, then reconciles
// with the authoritative server response, and reverts to a pre-action snapshot if the request fails.
/**
 * [onPolicyChanged] — ENG-2671: called after the server ACCEPTS an enforcement change, so the caller
 * can tell a running extension rather than leave it to discover the change on its next poll.
 * ENG-3416: and after it accepts a feature-flag change, which the extension reads from the same plan
 * (the deny list, Nano, Gmail scanning, SaaS reporting) and which cached console views depend on.
 * ENG-3434: and after it accepts a locked-policy or site-overrides save, which the plan carries too.
 *
 * A callback rather than importing the messaging helpers directly: the extension ids come from
 * `useExtension`, which owns detection, and duplicating that here would give the app two answers to
 * "which Haven extensions are installed".
 */
export function useOrgSettings(onPolicyChanged?: () => void) {
  const [enforcement, setEnforcement] = useState<EnforcementState | null>(null);
  const [featureFlags, setFeatureFlagsState] = useState<TenantFeatureFlags | null>(null);
  // A ref mirror of the flags, so an in-flight toggle always reads the LATEST state rather than the
  // value captured in its closure. Toggling flag B while flag A is still saving used to mean A's
  // failure reverted to a snapshot taken before B existed, silently wiping B from the UI while the
  // server kept it — see the revert path in setFeatureForced.
  const flagsRef = useRef<TenantFeatureFlags | null>(null);
  const setFeatureFlags = useCallback((next: TenantFeatureFlags | null) => {
    flagsRef.current = next;
    setFeatureFlagsState(next);
  }, []);
  // Only the newest issued flag request may replace the whole object with a server response: two
  // successful toggles whose responses land out of order would otherwise apply the older snapshot,
  // showing a flag as un-toggled until the next refetch.
  /**
   * ENG-2722: the caller's callback lives in a ref, not in a dependency array.
   *
   * `App.tsx` passes an inline arrow, so `onPolicyChanged` is a fresh identity every render.
   * Depending on it would make `changeEnforcement` — and therefore this hook's whole returned
   * object — unstable on every render, which is precisely what the memoisation exists to prevent.
   * The ref is always current, so the callback still fires with the latest closure. Same idiom as
   * `flagsRef` above, and for the same class of reason.
   */
  const onPolicyChangedRef = useRef(onPolicyChanged);
  onPolicyChangedRef.current = onPolicyChanged;

  const flagSeq = useRef(0);
  // ENG-3059: the tenant tier of the notification-category preference. Same ref-mirror + sequence
  // discipline as the flags above, and for the same two reasons — an in-flight write must read the
  // LATEST state rather than its closure's, and an older response must not replace a newer one.
  const [notificationPrefs, setNotificationPrefsState] = useState<TenantNotificationPreferences | null>(null);
  const notificationPrefsRef = useRef<TenantNotificationPreferences | null>(null);
  const setNotificationPrefs = useCallback((next: TenantNotificationPreferences | null) => {
    notificationPrefsRef.current = next;
    setNotificationPrefsState(next);
  }, []);
  const notificationSeq = useRef(0);
  const [notificationStatus, setNotificationStatus] = useState<Record<string, SaveStatus>>({});
  const [lockedPolicy, setLockedPolicy] = useState<LockedPolicy | null>(null);
  // ENG-3139: the tenant's site tier/tracked overrides + the catalog pick-list they are chosen from.
  const [siteOverrides, setSiteOverrides] = useState<SiteOverrides | null>(null);
  const [catalogSites, setCatalogSites] = useState<CatalogSite[] | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  // False until the first fetch resolves, so the page shows a spinner instead of an empty section.
  const [orgSettingsLoaded, setOrgSettingsLoaded] = useState(false);
  const [flagStatus, setFlagStatus] = useState<Record<string, SaveStatus>>({});
  const [enforcementStatus, setEnforcementStatus] = useState<SaveStatus | null>(null);

  const fetchOrgSettings = useCallback(async () => {
    if (!auth.currentUser) return;
    setSettingsError(null);
    try {
      const [enfRes, ffRes, lpRes, soRes, csRes, npRes] = await Promise.all([
        authenticatedFetch("/business/enforcement"),
        authenticatedFetch("/business/feature-flags"),
        authenticatedFetch("/business/locked-policy"),
        authenticatedFetch("/business/site-overrides"),
        authenticatedFetch("/business/catalog-sites"),
        authenticatedFetch("/business/notification-preferences"),
      ]);
      if (enfRes.ok) setEnforcement(await enfRes.json());
      if (ffRes.ok) setFeatureFlags(await ffRes.json());
      if (lpRes.ok) setLockedPolicy(await lpRes.json());
      if (soRes.ok) setSiteOverrides(await soRes.json());
      if (csRes.ok) setCatalogSites(await csRes.json());
      if (npRes.ok) setNotificationPrefs(await npRes.json());
      const results = [enfRes, ffRes, lpRes, soRes, csRes, npRes];
      if (results.every((r) => !r.ok)) setSettingsError("Could not load your organization settings.");
      else if (results.some((r) => !r.ok)) setSettingsError("Some settings could not be loaded. Try refreshing.");
    } catch (err) {
      console.error("Error fetching org settings:", err);
      setSettingsError("Could not load your organization settings.");
    } finally {
      setOrgSettingsLoaded(true);
    }
  }, [setFeatureFlags, setNotificationPrefs]);

  // Clear a "saved" tick after a beat - but only if a newer action hasn't since replaced it.
  const clearFlagSavedLater = useCallback((featureKey: string) => {
    setTimeout(() => {
      setFlagStatus((s) => {
        if (s[featureKey] !== "saved") return s;
        const next = { ...s };
        delete next[featureKey];
        return next;
      });
    }, 2000);
  }, []);

  // Switch enforcement mode. The backend 409s on switch-to-Active while the monitor lock is active;
  // the optimistic flip reverts and the reason surfaces via the returned error.
  const changeEnforcement = useCallback(async (mode: "monitor" | "enforce"): Promise<Result> => {
    const snapshot = enforcement;
    setEnforcementStatus("saving");
    if (enforcement) setEnforcement({ ...enforcement, mode }); // optimistic
    try {
      const res = await authenticatedFetch("/business/enforcement", { method: "PUT", body: JSON.stringify({ mode }) });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setEnforcement(data); // reconcile with the authoritative server state
        // Only on success, and only after the server agreed — an optimistic push would tell the
        // extension to enforce a mode the backend may have just refused (the 409 monitor lock).
        onPolicyChangedRef.current?.();
        setEnforcementStatus("saved");
        setTimeout(() => setEnforcementStatus((s) => (s === "saved" ? null : s)), 2000);
        return { success: true };
      }
      setEnforcement(snapshot); // revert (e.g. the 409 monitor-lock)
      setEnforcementStatus("error");
      return { success: false, error: data.error || "Could not change enforcement mode" };
    } catch (err) {
      setEnforcement(snapshot);
      setEnforcementStatus("error");
      return { success: false, error: err instanceof Error ? err.message : "Could not change enforcement mode" };
    }
  }, [enforcement]);

  // Force a tenant feature flag to a tri-state (ENG-2520): 'on'/'off' set an explicit tenant override
  // (PUT enabled:true|false), 'inherit' clears it back to the Haven default (DELETE). Unlike the old
  // on/inherit switch, a tenant can now force a flag explicitly OFF — the group tier already had this
  // idiom (ManageGroupPolicyDialog); this brings the tenant tier to parity.
  const setFeatureForced = useCallback(async (featureKey: string, state: "inherit" | "on" | "off"): Promise<Result> => {
    // Restores ONLY this key, on top of whatever the flags are when the failure lands — never a
    // whole-object snapshot. Reverting wholesale would undo any other flag the admin changed while
    // this request was in flight, leaving the UI claiming a policy the server does not have. The
    // section disables only the flag being saved, so a second flag is always clickable mid-flight.
    const before = flagsRef.current;
    const revertThisKey = () => {
      const cur = flagsRef.current;
      if (!cur || !before) return;
      const resolved = { ...cur.resolved };
      const tenantOverrides = { ...cur.tenantOverrides };
      if (featureKey in before.tenantOverrides) tenantOverrides[featureKey] = before.tenantOverrides[featureKey];
      else delete tenantOverrides[featureKey];
      resolved[featureKey] = before.resolved[featureKey];
      setFeatureFlags({ ...cur, resolved, tenantOverrides });
    };

    const seq = ++flagSeq.current;
    setFlagStatus((s) => ({ ...s, [featureKey]: "saving" }));
    // Optimistic: compute the resolved + override state now. 'inherit' clears the override, so the
    // resolved value reverts to the global default (which the response already carries in `global`);
    // 'on'/'off' pin both the override and the resolved value to the forced boolean.
    if (before) {
      const resolved = { ...before.resolved };
      const tenantOverrides = { ...before.tenantOverrides };
      if (state === "inherit") {
        delete tenantOverrides[featureKey];
        resolved[featureKey] = before.global[featureKey] ?? false;
      } else {
        const on = state === "on";
        tenantOverrides[featureKey] = on;
        resolved[featureKey] = on;
      }
      setFeatureFlags({ ...before, resolved, tenantOverrides });
    }
    try {
      const res = state === "inherit"
        ? await authenticatedFetch(`/business/feature-flags/${featureKey}`, { method: "DELETE" })
        : await authenticatedFetch("/business/feature-flags", { method: "PUT", body: JSON.stringify({ featureKey, enabled: state === "on" }) });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        // Reconcile wholesale only if nothing newer has been issued; an older response carries a
        // server snapshot that predates the newer toggle's write. The optimistic value stands either
        // way, and the next fetchOrgSettings reconciles for real.
        if (seq === flagSeq.current) setFeatureFlags(data);
        // ENG-3416: announced like an enforcement change, and only once the server has accepted it.
        onPolicyChangedRef.current?.();
        setFlagStatus((s) => ({ ...s, [featureKey]: "saved" }));
        clearFlagSavedLater(featureKey);
        return { success: true };
      }
      revertThisKey();
      setFlagStatus((s) => ({ ...s, [featureKey]: "error" }));
      return { success: false, error: data.error || "Could not update feature flag" };
    } catch (err) {
      revertThisKey();
      setFlagStatus((s) => ({ ...s, [featureKey]: "error" }));
      return { success: false, error: err instanceof Error ? err.message : "Could not update feature flag" };
    }
  }, [setFeatureFlags, clearFlagSavedLater]);

  // Back-compat 2-state wrapper (ON → override true, OFF → clear override). Retained so the existing
  // semantics + tests still hold; new callers use the tri-state setFeatureForced directly.
  const toggleFeatureFlag = useCallback(
    (featureKey: string, enabled: boolean): Promise<Result> =>
      setFeatureForced(featureKey, enabled ? "on" : "inherit"),
    [setFeatureForced],
  );

  // Clear a "saved" tick after a beat, unless a newer action has since replaced it.
  const clearNotificationSavedLater = useCallback((category: string) => {
    setTimeout(() => {
      setNotificationStatus((s) => {
        if (s[category] !== "saved") return s;
        const next = { ...s };
        delete next[category];
        return next;
      });
    }, 2000);
  }, []);

  /**
   * Set the ORG DEFAULT for one notification category (ENG-3059). Tri-state: 'on'/'off' write an
   * explicit tenant value, 'inherit' DELETEs it back to the Haven default.
   *
   * Optimistic, with a per-key revert rather than a whole-object snapshot — reverting wholesale
   * would undo any other category changed while this request was in flight, leaving the UI claiming
   * a policy the server does not have. Exactly the failure `setFeatureForced` documents above.
   */
  const setNotificationForced = useCallback(async (category: string, state: "inherit" | "on" | "off"): Promise<Result> => {
    const before = notificationPrefsRef.current;
    const revertThisKey = () => {
      const cur = notificationPrefsRef.current;
      if (!cur || !before) return;
      const resolved = { ...cur.resolved };
      const tenantOverrides = { ...cur.tenantOverrides };
      if (category in before.tenantOverrides) tenantOverrides[category] = before.tenantOverrides[category];
      else delete tenantOverrides[category];
      resolved[category] = before.resolved[category];
      setNotificationPrefs({ ...cur, resolved, tenantOverrides });
    };

    const seq = ++notificationSeq.current;
    setNotificationStatus((s) => ({ ...s, [category]: "saving" }));
    if (before) {
      const resolved = { ...before.resolved };
      const tenantOverrides = { ...before.tenantOverrides };
      if (state === "inherit") {
        delete tenantOverrides[category];
        resolved[category] = before.defaults[category] ?? false;
      } else {
        const on = state === "on";
        tenantOverrides[category] = on;
        resolved[category] = on;
      }
      setNotificationPrefs({ ...before, resolved, tenantOverrides });
    }
    try {
      const res = state === "inherit"
        ? await authenticatedFetch(`/business/notification-preferences/${category}`, { method: "DELETE" })
        : await authenticatedFetch("/business/notification-preferences", {
            method: "PUT",
            body: JSON.stringify({ category, enabled: state === "on" }),
          });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (seq === notificationSeq.current) setNotificationPrefs(data);
        setNotificationStatus((s) => ({ ...s, [category]: "saved" }));
        clearNotificationSavedLater(category);
        return { success: true };
      }
      revertThisKey();
      setNotificationStatus((s) => ({ ...s, [category]: "error" }));
      return { success: false, error: data.error || "Could not update notification preference" };
    } catch (err) {
      revertThisKey();
      setNotificationStatus((s) => ({ ...s, [category]: "error" }));
      return { success: false, error: err instanceof Error ? err.message : "Could not update notification preference" };
    }
  }, [setNotificationPrefs, clearNotificationSavedLater]);

  const saveLockedPolicy = useCallback(async (policy: LockedPolicy): Promise<Result> => {
    try {
      const res = await authenticatedFetch("/business/locked-policy", { method: "PUT", body: JSON.stringify(policy) });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setLockedPolicy(data);
        // ENG-3434: the locked and denied categories and sites are in the plan the extension reads
        // (/subscription-status), so announced like a flag change, and only once the server accepted.
        onPolicyChangedRef.current?.();
        return { success: true };
      }
      return { success: false, error: data.error || "Could not save locked policy" };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : "Could not save locked policy" };
    }
  }, []);

  /** ENG-3139: replace-all PUT of the tenant's site overrides; the stored list comes back on success. */
  const saveSiteOverrides = useCallback(async (overrides: SiteOverride[]): Promise<Result> => {
    try {
      const res = await authenticatedFetch("/business/site-overrides", { method: "PUT", body: JSON.stringify({ overrides }) });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSiteOverrides(data);
        // ENG-3434: the extension reads these from the same plan (`siteOverrides`). See saveLockedPolicy.
        onPolicyChangedRef.current?.();
        return { success: true };
      }
      return { success: false, error: data.error || "Could not save site overrides" };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : "Could not save site overrides" };
    }
  }, []);

  const reset = useCallback(() => {
    setEnforcement(null);
    setFeatureFlags(null);
    setNotificationPrefs(null);
    setLockedPolicy(null);
    setSiteOverrides(null);
    setCatalogSites(null);
    setSettingsError(null);
    setOrgSettingsLoaded(false);
    setFlagStatus({});
    setNotificationStatus({});
    setEnforcementStatus(null);
  }, [setFeatureFlags, setNotificationPrefs]);

  // Memoised like the other org hooks (ENG-2722). Note this object DOES change when `enforcement`
  // does, because `changeEnforcement` reads it for the optimistic-update revert — so a call site
  // should depend on the specific function it calls, not on the object.
  return useMemo(
    () => ({
      enforcement,
      featureFlags,
      notificationPrefs,
      lockedPolicy,
      siteOverrides,
      catalogSites,
      settingsError,
      orgSettingsLoaded,
      flagStatus,
      notificationStatus,
      enforcementStatus,
      fetchOrgSettings,
      changeEnforcement,
      toggleFeatureFlag,
      setFeatureForced,
      setNotificationForced,
      saveLockedPolicy,
      saveSiteOverrides,
      reset,
    }),
    [enforcement, featureFlags, notificationPrefs, lockedPolicy, siteOverrides, catalogSites, saveSiteOverrides, settingsError, orgSettingsLoaded, flagStatus, notificationStatus, enforcementStatus, fetchOrgSettings, changeEnforcement, toggleFeatureFlag, setFeatureForced, setNotificationForced, saveLockedPolicy, reset],
  );
}
